// BOORO: the inbox in the Start area: everything that waits for a person, over
// every office of the account. The filter narrows it like the task board.

import { formatCost } from "@booro/tasks";
import type { OrgScope } from "@booro/team";
import { itemInScope } from "@booro/team";
import { Trans, useLingui } from "@lingui/react/macro";
import type {
  Artifact,
  InboxItem,
  InboxKind,
  LoginFieldInput,
  OrgSnapshot,
} from "@rakazo/contracts";
import { BOT_COLORS } from "@rakazo/contracts";
import { isSecretAskBlock } from "@rakazo/core";
import { BotAvatar, Button } from "@rakazo/ui-web";
import {
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Hand,
  MessageCircleQuestion,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import type { MouseEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArtifactGallery, artifactGalleryItem } from "../../components/ArtifactGallery";
import { AskCard } from "../../components/AskCard";
import { TaskReviewActions } from "../../components/TaskReviewActions";
import { formatRelativeTime } from "../../lib/relative-time";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { DashboardView } from "./dashboard";
import { officeRpc, openAgentChat, ScopeFilter, useOrgScope, useOrgSnapshot } from "./org-scope";
import type { WorkspaceChrome } from "./workspace-header";
import { WorkspaceViewHeader } from "./workspace-header";

/** How often the inbox reloads while the Start area is open. */
const REFRESH_MS = 15_000;

const KIND_ICONS: Record<InboxKind, typeof ShieldCheck> = {
  approval: ShieldCheck,
  question: MessageCircleQuestion,
  takeover: Hand,
  review: CircleCheck,
  failure: CircleAlert,
  budget: Wallet,
};

/** The heading each kind gets above its cards. */
function useKindLabels(): Record<InboxKind, string> {
  const { t } = useLingui();
  return {
    approval: t`Approvals`,
    question: t`Questions`,
    takeover: t`Takeovers`,
    review: t`To review`,
    failure: t`Failures`,
    budget: t`Budgets`,
  };
}

/** The entries of the filter: one office, a group with its agents, or one agent. */
export function inboxItemsInScope(
  snapshot: OrgSnapshot | null,
  scope: OrgScope,
  items: readonly InboxItem[],
): InboxItem[] {
  if (!snapshot) return [...items];
  return items.filter((item) =>
    itemInScope(
      snapshot.groups,
      snapshot.agents,
      { spaceId: item.spaceId, botId: item.botId },
      scope,
    ),
  );
}

export function InboxView({ title, chrome }: { title: string; chrome: WorkspaceChrome }) {
  const { snapshot } = useOrgSnapshot();
  const [scope, setScope] = useOrgScope();
  const [items, setItems] = useState<InboxItem[]>([]);
  const [counts, setCounts] = useState<Record<InboxKind, number>>({
    approval: 0,
    question: 0,
    takeover: 0,
    review: 0,
    failure: 0,
    budget: 0,
  });
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const next = await rpc.booroInbox.list();
      setItems(next.items);
      setCounts(next.counts);
      setLoaded(true);
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    }
  }, []);

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [reload]);

  // The list covers the account; the filter shows one office, group or agent.
  const narrowed = Boolean(scope.spaceId || scope.groupId || scope.botId);
  const shown = useMemo(() => inboxItemsInScope(snapshot, scope, items), [items, snapshot, scope]);

  const groups = useMemo(() => {
    const byKind = new Map<InboxKind, InboxItem[]>();
    for (const item of shown) {
      const list = byKind.get(item.kind);
      if (list) list.push(item);
      else byKind.set(item.kind, [item]);
    }
    return [...byKind.entries()];
  }, [shown]);

  const severalPlaces = (snapshot?.offices.length ?? 0) > 1 || (snapshot?.agents.length ?? 0) > 1;

  return (
    <>
      <WorkspaceViewHeader title={title} chrome={chrome} />
      {snapshot && severalPlaces ? (
        <div
          data-testid="start-filters"
          className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2"
        >
          <ScopeFilter snapshot={snapshot} scope={scope} onChange={setScope} />
        </div>
      ) : null}
      <div className="rk-scroll flex-1 overflow-y-auto px-4 pt-4 pb-24 md:px-6">
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {loaded && shown.length === 0 && !error ? (
          <p className="text-sm text-muted-foreground">
            <Trans>Nothing to do.</Trans>
          </p>
        ) : null}
        {groups.map(([kind, group]) => (
          <InboxGroup
            key={kind}
            kind={kind}
            items={group}
            // The count of every office does not describe a narrowed list.
            count={narrowed ? group.length : counts[kind]}
            onChange={reload}
          />
        ))}
        {/* BOORO: the dashboard sits under the inbox: what was delivered comes after what waits. */}
        <div className="mt-8">
          <DashboardView snapshot={snapshot} scope={scope} />
        </div>
      </div>
    </>
  );
}

function InboxGroup({
  kind,
  items,
  count,
  onChange,
}: {
  kind: InboxKind;
  items: InboxItem[];
  count: number;
  onChange: () => Promise<void>;
}) {
  const labels = useKindLabels();
  const Icon = KIND_ICONS[kind];
  return (
    <section className="mb-6" data-inbox-kind={kind}>
      <h2 className="mb-2 flex items-center gap-2 text-[13px] font-semibold tracking-wider uppercase text-muted-foreground">
        <Icon size={15} strokeWidth={1.8} aria-hidden="true" />
        {labels[kind]} ({count})
      </h2>
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <InboxCard key={item.key} item={item} onChange={onChange} />
        ))}
      </div>
    </section>
  );
}

/** How many images a review card shows before the last tile counts the rest. */
const REVIEW_IMAGE_LIMIT = 4;

/** What the task produced, so the decision can be made from the card. */
function ReviewDeliverables({ spaceId, ticketId }: { spaceId: string; ticketId: string }) {
  const [deliverables, setDeliverables] = useState<readonly Artifact[]>([]);
  useEffect(() => {
    let active = true;
    void officeRpc(spaceId)
      .booroTasks.deliverables({ ticketId })
      .then((next) => {
        if (active) setDeliverables(next);
      })
      // The card still offers the decision without its files.
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [spaceId, ticketId]);
  if (deliverables.length === 0) return null;
  return (
    // The viewer renders in a portal whose clicks would still reach the card.
    // biome-ignore lint/a11y: only stops clicks from opening the chat behind.
    <div className="mt-3" onClick={(event) => event.stopPropagation()}>
      <ArtifactGallery
        target={{ ticketId, spaceId }}
        imageLimit={REVIEW_IMAGE_LIMIT}
        items={deliverables.map(artifactGalleryItem)}
      />
    </div>
  );
}

function InboxCardShell({
  item,
  onOpen,
  className,
  children,
}: {
  item: InboxItem;
  onOpen: () => void;
  className: string;
  children: ReactNode;
}) {
  function openFromCard(event: MouseEvent<HTMLDivElement>) {
    if (!item.botId) return;
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest("button, a, input, textarea, select, [role='button']")
    )
      return;
    onOpen();
  }

  return (
    // biome-ignore lint/a11y: the header is the keyboard target; the card surface is a mouse shortcut.
    <div
      data-testid={`inbox-item-${item.key}`}
      onClick={openFromCard}
      className={`${item.botId ? "cursor-pointer " : ""}${className}`}
    >
      {children}
    </div>
  );
}

export function InboxCard({ item, onChange }: { item: InboxItem; onChange: () => Promise<void> }) {
  const { t, i18n } = useLingui();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const task = "task" in item ? item.task : null;

  const openChat = () => {
    if (!item.botId) return;
    const params = new URLSearchParams();
    if (item.kind === "takeover" && "messageId" in item) params.set("m", item.messageId);
    else if (task?.ticketId) params.set("task", task.ticketId);
    else if ("messageId" in item) params.set("m", item.messageId);
    const query = params.toString();
    // The entry belongs to its office, so opening it may switch the office.
    openAgentChat(item.spaceId, `/app/${item.botId}${query ? `?${query}` : ""}`);
  };

  async function answer(
    text: string,
    fields?: LoginFieldInput[],
    remember?: false,
    dismiss?: true,
  ) {
    if (!("runId" in item) || !("messageId" in item)) return;
    setBusy(true);
    setError("");
    try {
      await officeRpc(item.spaceId).threads.answer({
        botId: item.botId,
        runId: item.runId,
        messageId: item.messageId,
        answer: text,
        ...(fields ? { fields } : {}),
        ...(remember === false ? { remember } : {}),
        ...(dismiss ? { dismiss } : {}),
      });
      await onChange();
    } catch (cause) {
      // Answering twice is not an error the person has to fix; the entry is gone.
      setError(errorText(cause, t`Could not submit this answer`));
      await onChange();
    } finally {
      setBusy(false);
    }
  }

  const headContent = (
    <>
      {item.botId ? (
        <BotAvatar color={item.botColor ?? BOT_COLORS[1]} identity={item.botId} size={22} />
      ) : null}
      <span className="min-w-0 flex-1 truncate" dir="auto">
        {item.botName ?? t`Task`}
        {task ? ` · ${task.ref} ${task.title}` : null}
      </span>
      <time className="shrink-0" dateTime={item.createdAt}>
        {formatRelativeTime(item.createdAt)}
      </time>
      {item.botId ? (
        <ChevronRight size={14} aria-hidden="true" className="shrink-0 text-muted-foreground/70" />
      ) : null}
    </>
  );
  const head = item.botId ? (
    <button
      type="button"
      onClick={openChat}
      className="flex w-full min-w-0 items-center gap-2 text-start text-[13px] text-muted-foreground"
    >
      {headContent}
    </button>
  ) : (
    <div className="flex w-full min-w-0 items-center gap-2 text-[13px] text-muted-foreground">
      {headContent}
    </div>
  );

  if (item.kind === "approval" || item.kind === "question") {
    // A secret is answered in the chat, where the masked field lives.
    if (item.block.input === "secret") {
      return (
        <InboxCardShell
          item={item}
          onOpen={openChat}
          className="rounded-2xl border border-border bg-card px-4 py-3"
        >
          {head}
          <p className="mt-1 text-[15px] text-foreground" dir="auto">
            {item.block.text}
          </p>
          <Button className="mt-3" variant="outline" onClick={openChat}>
            <Trans>Answer in chat</Trans>
          </Button>
        </InboxCardShell>
      );
    }
    return (
      <InboxCardShell
        item={item}
        onOpen={openChat}
        className="rounded-2xl border border-border bg-card"
      >
        <div className="px-4 pt-3">{head}</div>
        <div className="p-4 pt-2">
          <AskCard
            block={item.block as Parameters<typeof AskCard>[0]["block"]}
            canAnswer={!busy}
            canDismiss={!busy && isSecretAskBlock({ kind: "ask", ...item.block })}
            onAnswer={answer}
            onDismiss={() => answer("", undefined, undefined, true)}
          />
        </div>
        {error ? (
          <p role="alert" className="px-4 pb-3 text-[13px] text-destructive">
            {error}
          </p>
        ) : null}
      </InboxCardShell>
    );
  }

  if (item.kind === "takeover") {
    return (
      <InboxCardShell
        item={item}
        onOpen={openChat}
        className="rounded-2xl border border-border bg-card px-4 py-3"
      >
        {head}
        <p className="mt-1 text-[15px] text-foreground">
          <Trans>Needs you to take over the computer.</Trans>
        </p>
        {task?.result ? (
          <p className="mt-1 text-[14px] text-foreground/80" dir="auto">
            {task.result}
          </p>
        ) : null}
        <Button className="mt-3" variant="outline" onClick={openChat}>
          <Trans>Open chat</Trans>
        </Button>
      </InboxCardShell>
    );
  }

  if (item.kind === "review") {
    return (
      <InboxCardShell
        item={item}
        onOpen={openChat}
        className="rounded-2xl border border-border bg-card px-4 py-3"
      >
        {head}
        <p className="mt-1 text-[15px] font-medium text-foreground" dir="auto">
          {item.task.title}
        </p>
        {item.task.result ? (
          <p className="mt-1 text-[14px] text-foreground/80" dir="auto">
            {item.task.result}
          </p>
        ) : null}
        <ReviewDeliverables spaceId={item.spaceId} ticketId={item.task.ticketId} />
        <div className="mt-3">
          <TaskReviewActions
            ticketId={item.task.ticketId}
            client={officeRpc(item.spaceId)}
            onChanged={onChange}
          />
        </div>
      </InboxCardShell>
    );
  }

  if (item.kind === "budget") {
    return (
      <InboxCardShell
        item={item}
        onOpen={openChat}
        className="rounded-2xl border border-border bg-card px-4 py-3"
      >
        {head}
        <p className="mt-1 text-[15px] font-medium text-foreground">
          {item.level === "limit" ? (
            <Trans>Monthly budget used up</Trans>
          ) : (
            <Trans>Monthly budget nearly used up</Trans>
          )}
        </p>
        <p className="mt-1 text-[14px] text-foreground/80">
          <Trans>
            {formatCost(item.spentUsd, i18n.locale)} of {formatCost(item.monthlyUsd, i18n.locale)}{" "}
            this month
          </Trans>
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.dispatchEvent(new Event("booro:open-budgets"))}
            disabled={busy}
          >
            <Trans>Open budgets</Trans>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                await rpc.booroInbox.dismiss({ itemKey: item.key, spaceId: item.spaceId });
                await onChange();
              } catch (cause) {
                setError(errorText(cause));
              } finally {
                setBusy(false);
              }
            }}
          >
            <Trans>Dismiss</Trans>
          </Button>
        </div>
        {error ? (
          <p role="alert" className="mt-2 text-[13px] text-destructive">
            {error}
          </p>
        ) : null}
      </InboxCardShell>
    );
  }

  return (
    <InboxCardShell
      item={item}
      onOpen={openChat}
      className="rounded-2xl border border-border bg-card px-4 py-3"
    >
      {head}
      <p className="mt-1 text-[15px] font-medium text-foreground" dir="auto">
        {item.error ?? item.task?.title ?? t`Task failed`}
      </p>
      {item.task?.result && item.task.result !== item.error ? (
        <p className="mt-1 text-[14px] text-foreground/80" dir="auto">
          {item.task.result}
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {item.task ? (
          <Button
            size="sm"
            disabled={busy}
            onClick={async () => {
              const ticketId = item.task?.ticketId;
              if (!ticketId) return;
              setBusy(true);
              setError("");
              try {
                await officeRpc(item.spaceId).tickets.update({ id: ticketId, status: "todo" });
                await onChange();
              } catch (cause) {
                setError(errorText(cause));
              } finally {
                setBusy(false);
              }
            }}
          >
            <Trans>Try again</Trans>
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError("");
            try {
              await rpc.booroInbox.dismiss({ itemKey: item.key, spaceId: item.spaceId });
              await onChange();
            } catch (cause) {
              setError(errorText(cause));
            } finally {
              setBusy(false);
            }
          }}
        >
          <Trans>Dismiss</Trans>
        </Button>
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-[13px] text-destructive">
          {error}
        </p>
      ) : null}
    </InboxCardShell>
  );
}

/** The number of entries waiting for the person, shown as a badge in the sidebar. */
export function useInboxCount(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let active = true;
    const load = () =>
      rpc.booroInbox
        .count()
        .then((next) => {
          if (active) setCount(next.count);
        })
        .catch(() => undefined);
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);
  return count;
}
